import { Star, MapPin, Home } from "lucide-react";
import { cn } from "@/lib/utils";

export type ServiceCardReview = {
  id: number;
  nota: number;
  comentario: string;
  tem_comentario?: boolean;
  tutor_nome: string;
  servico_nome?: string | null;
  data?: string | null;
};

export type ServiceCardItem = {
  servico_id: number;
  tipo_servico?: {
    nome?: string | null;
    tipo_precificacao?: string | null;
  } | null;
  anfitriao?: {
    id?: number | null;
    nome?: string | null;
    cidade?: string | null;
    bairro?: string | null;
    regiao?: string | null;
    endereco?: string | null;
  } | null;
  imagens?: string[];
  preco_base?: number | null;
  preco_exibicao?: number | null;
  precos_detalhados?: Array<{ data?: string; preco?: number | string }>;
  vagas?: {
    limite_pets: number;
    vagas_disponiveis: number;
    label?: string;
  } | null;
  capacidade?: {
    pets_simultaneos?: number | null;
    pets_label?: string | null;
    max_atendimentos_dia?: number | null;
    atendimentos_disponiveis?: number | null;
    atendimentos_label?: string | null;
  } | null;
  capacidade_diaria?: {
    limite?: number | null;
    reservas_do_dia?: number | null;
    disponiveis?: number | null;
    label?: string | null;
  } | null;
  avaliacoes?: {
    media: number;
    total: number;
    texto?: string | null;
    historico?: ServiceCardReview[];
  } | null;
  focal_y?: number | null;
};

function money(v?: number | null) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function imgUrl(url?: string) {
  if (!url) return "/assets/img/placeholder.png";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/")) return url;
  return `/${url}`;
}

function cleanLabel(label?: string | null) {
  return String(label || "Hospeda até").replace(/^[^\p{L}\p{N}]+/u, "").trim();
}

function petPlural(total: number) {
  return total === 1 ? "pet" : "pets";
}

export function ServiceCard({ it, onReviewsClick }: { it: ServiceCardItem; onReviewsClick?: (item: ServiceCardItem) => void }) {
  const nome = it.anfitriao?.nome || "Anfitrião";
  const cidade = it.anfitriao?.cidade || "";
  const bairro = it.anfitriao?.bairro || "";
  const regiao = it.anfitriao?.regiao || "";
  const servico = it.tipo_servico?.nome || "Serviço";
  const foto = imgUrl(it.imagens?.[0]);
  const precoExibicao = it.preco_exibicao ?? Number(it.precos_detalhados?.[0]?.preco ?? it.preco_base ?? 0);
  const tipoPrecificacao = it.tipo_servico?.tipo_precificacao ?? "diaria";
  const isServicoHorario = tipoPrecificacao === "horaria";
  const rating = Number(it.avaliacoes?.media || 0);
  const total = Number(it.avaliacoes?.total || 0);
  const vagas = it.vagas?.vagas_disponiveis ?? null;
  const limite = it.vagas?.limite_pets ?? null;
  const vagasLabel = cleanLabel(it.vagas?.label);
  const petsSimultaneos = it.capacidade?.pets_simultaneos ?? null;
  const localizacao = [cidade, regiao, bairro].filter((value, index, arr) => value && arr.indexOf(value) === index).join(" · ");

  return (
    <article className={cn("group block rounded-2xl border bg-background shadow-sm transition hover:shadow-md", "focus:outline-none focus:ring-2 focus:ring-ring")}>
      <div className="flex gap-4 p-4">
        <div className="h-[92px] w-[110px] shrink-0 overflow-hidden rounded-xl bg-muted flex items-center justify-center">
          <img src={foto} alt={nome} className="h-full w-full object-contain transition group-hover:scale-[1.02]" style={{ objectPosition: "center center" }} loading="lazy" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-xs text-muted-foreground">{servico}</div>
          <div className="mt-0.5 truncate font-semibold leading-tight">{nome}</div>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="truncate">{localizacao || cidade}</span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs">
              <Star className={cn("h-3.5 w-3.5", total > 0 && "fill-[#d4af37] text-[#d4af37]")} />
              {total > 0 ? <><b>{rating.toFixed(1)}</b><span className="text-muted-foreground">({total})</span></> : <span className="text-muted-foreground">Novo anfitrião</span>}
            </span>

            <button type="button" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onReviewsClick?.(it); }} className="inline-flex items-center rounded-full bg-white px-2 py-1 text-xs font-semibold text-forest ring-1 ring-forest/20 transition hover:bg-forest-light">
              Ver avaliações
            </button>

            {petsSimultaneos != null && petsSimultaneos > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs"><Home className="h-3.5 w-3.5" />Até <b>{petsSimultaneos}</b> {petPlural(petsSimultaneos)} por vez</span>
            )}

            {!isServicoHorario && petsSimultaneos == null && limite != null && limite > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs"><Home className="h-3.5 w-3.5" />Até <b>{limite}</b> {petPlural(limite)} por vez</span>
            )}

            {!isServicoHorario && vagas != null && (
              <span className={cn("inline-flex items-center rounded-full px-2 py-1 text-xs", vagas <= 1 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800")}>{vagas <= 1 ? "Última vaga" : `${vagas} vagas`}</span>
            )}

            {isServicoHorario && (petsSimultaneos == null || petsSimultaneos <= 0) && (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs text-emerald-800">Horários disponíveis</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col justify-between text-right">
          <div>
            <div className="text-xs text-muted-foreground">a partir de</div>
            <div className="text-lg font-semibold">{money(precoExibicao)}</div>
            <div className="text-xs text-muted-foreground">{tipoPrecificacao === "horaria" ? "/ hora" : tipoPrecificacao === "creche" ? "/ período" : "/ diária"}</div>
          </div>
          <div className="mt-3 inline-flex justify-end"><span className="rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground">Ver detalhes</span></div>
        </div>
      </div>
    </article>
  );
}
